import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, dir } = useLanguage();

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.solutions, path: '/solutions' },
    { name: t.nav.pricing, path: '/pricing' },
    { name: t.nav.contact, path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-sand-50/90 backdrop-blur-lg shadow-lg shadow-bark-700/5'
            : 'bg-transparent'
        }`}
        dir={dir}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-forest to-forest-400 flex items-center justify-center shadow-lg shadow-forest/25 group-hover:shadow-forest/40 transition-shadow">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="text-xl font-bold text-bark-700">
                Med<span className="text-forest">Kit</span>.ai
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === link.path
                      ? 'text-forest bg-forest/10'
                      : 'text-bark-700/60 hover:text-bark-700 hover:bg-sand-300/30'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons & Language */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher />
              <a
                href="https://www.medkit-ai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-bark-700/60 hover:text-bark-700 transition-colors"
              >
                {t.nav.signIn}
              </a>
              <Link
                to="/contact"
                className="px-5 py-2.5 rounded-xl bg-bark-700 text-white text-sm font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25 hover:shadow-bark-700/40 hover:-translate-y-0.5"
              >
                {t.nav.getStarted}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-sand-300/30 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-bark-700/60" />
              ) : (
                <Menu className="w-6 h-6 text-bark-700/60" />
              )}
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-20 z-40 md:hidden"
            dir={dir}
          >
            <div className="bg-sand-50 shadow-xl border-t border-sand-300 p-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-all ${
                    location.pathname === link.path
                      ? 'text-forest bg-forest/10'
                      : 'text-bark-700/60 hover:text-bark-700 hover:bg-sand-300/30'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="py-4 border-t border-sand-300">
                <LanguageSwitcher />
              </div>
              <div className="pt-4 border-t border-sand-300 space-y-3">
                <a
                  href="https://www.medkit-ai.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center px-4 py-3 rounded-xl text-base font-medium text-bark-700/60 hover:bg-sand-300/30 transition-colors"
                >
                  {t.nav.signIn}
                </a>
                <Link
                  to="/contact"
                  className="block text-center px-4 py-3 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-colors"
                >
                  {t.nav.getStarted}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
